import { Box, Card, CardContent, CardMedia, Chip, CircularProgress, Container, Grid, Typography } from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import appealableTemporaryOrdersImage from '../assets/images/appealable-temporary-orders.png';
import familyRightsHeroImage from '../assets/images/family-rights-due-process-act.png';
import bestInterestChildImage from '../assets/images/best-interests-child-courtroom-1000.png';
import Breadcrumbs from '../components/Breadcrumbs';
import NavigateToButton from '../components/NavigateToButton';
import StateShape from '../components/StateShape';
import SEO from '../components/SEO';
import { useBillDataSheets, useBillPages } from '../hooks/useBillData';
import type { BreadcrumbItem } from '../types/seo';
import { resolveBillRoute } from '../utils/billRoutes';
import { isValidStateSlug, slugToStateName, stateNameToSlug, countrySlugToName } from '../utils/stateUtils';
import { generateWebPageSchema } from '../utils/schemaGenerators';

// Module-level log to verify this file is being loaded
console.log('🚀 StateBillsPage.tsx module loaded at:', new Date().toISOString());

// Hero image mapping - reused from BillDetailPage
const heroImageMap: Record<string, string> = {
  'family-first-federal-reform': '/images/family-first-federal-reform.png',
  'a-new-start-at-equality': '/images/a-new-start-at-equality.png',
  'appealable-temporary-orders': appealableTemporaryOrdersImage,
  'cameras-in-courtrooms': '/images/cameras-in-courtrooms.png',
  'child-support-dual-calculation-reform': '/images/child-support-dual-calculation-reform.png',
  'child-support-integrity-act': '/images/child-support-integrity-act.png',
  'justice-delayed-is-justice-denied': '/images/justice-delayed-is-justice-denied.png',
  'litigant-access-economy-act': '/images/litigant-access-economy-bill.png',
  'three-strikes': '/images/three-strikes.png',
  'time-taken-time-back': '/images/time-taken-time-back.png',
  'reimbursement-fees-parental-alienation': '/images/reimbursement-fees-parental-alienation.png',
  'family-rights-due-process-act': familyRightsHeroImage,
  'best-interest-of-the-child-definition-act': bestInterestChildImage,
  'parent-child-restoration-act': '/images/parent-child-restoration-act.png',
  'judicial-accountability-referral-act': '/images/judicial-accountability-referral-act.png',
  _default: '/images/family-first-federal-reform.png', // fallback image
};

const getBillStatusChip = (billStatus: string): { color: 'warning' | 'success' | 'error'; label: string } => {
  const s = billStatus?.toLowerCase() || '';
  if (s.includes('in progress')) return { color: 'warning', label: billStatus };
  if (s.includes('passed')) return { color: 'success', label: billStatus };
  return { color: 'error', label: billStatus || 'Not Started' };
};

const StateBillsPage: React.FC = () => {
  const {
    state: stateParam,
    param,
    country: countryParam,
    seg1,
    seg2,
  } = useParams<{
    state?: string;
    param?: string;
    country?: string;
    seg1?: string;
    seg2?: string;
  }>();
  // When routed from BillRouter/BillTwoSegmentRouter, state comes from various params
  const state = stateParam || seg2 || param;
  const country = countryParam || seg1 || 'united-states';
  const countryName = countrySlugToName(country) || 'United States';
  const { data: bills, isLoading, error } = useBillDataSheets();
  const { data: billPages } = useBillPages();

  // Build title → shortDescription lookup so cards show the bill description,
  // not the advocate name stored in the Bill Data sheet's notes column.
  const shortDescriptionByTitle = React.useMemo(() => {
    if (!billPages) return {};
    return Object.fromEntries(billPages.map((bp) => [bp.pageTitle.toLowerCase(), bp.shortDescription]));
  }, [billPages]);

  console.log('🎯 StateBillsPage rendered');
  console.log('📍 state param from URL:', state);

  // Check if this is a valid state slug
  const isState = state && isValidStateSlug(state);
  const stateName = state ? slugToStateName(state) : 'Unknown State';

  console.log('🏷️ Is valid state:', isState);
  console.log('🏷️ State name:', stateName);
  console.log('📊 Bills data:', bills ? `${bills.length} bills loaded` : 'No bills');

  // Filter bills for this state - MUST be before any conditional returns due to Rules of Hooks
  const stateBills = React.useMemo(() => {
    if (!bills || !state) return [];
    const filtered = bills.filter((bill) => {
      const billStateSlug = stateNameToSlug(bill.state);
      return billStateSlug === state;
    });
    console.log('🔍 Filtered bills for state:', filtered.length);
    return filtered;
  }, [bills, state]);

  // If not a valid state, show error
  if (!isState) {
    console.log('❌ Invalid state slug:', state);
    return (
      <Container component="main" sx={{ py: 8 }}>
        <SEO
          title="RobertGarza.us Family Court Reform - Invalid State"
          description="The requested state page could not be found."
          path={`/bill/${country}/${state || 'unknown'}`}
        />
        <Typography variant="h2" sx={{ textAlign: 'center', mb: 4 }}>
          Invalid State
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center' }}>
          The state you're looking for could not be found. Please select a valid state.
        </Typography>
      </Container>
    );
  }

  // Generate breadcrumbs
  const breadcrumbs: BreadcrumbItem[] = [
    { name: 'Home', url: '/', position: 1 },
    { name: 'Bills', url: '/family-court-reform-bills', position: 2 },
    { name: countryName, position: 3 },
    { name: stateName, url: `/bill/${country}/${state}`, position: 4 },
  ];

  // Generate ItemList schema for AI engines
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${stateName} Family Law Reform Bills`,
    description: `Complete list of family law reform bills for ${stateName}`,
    numberOfItems: stateBills.length,
    itemListElement: stateBills.map((bill, index) => {
      const billSlug = resolveBillRoute(bill.billTitle, billPages);

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'LegislativeProposal',
          name: bill.billTitle,
          description: bill.notes || `Family law reform legislation for ${stateName}`,
          url: `https://robertgarza.us/bill/${country}/${state}/${billSlug}`,
          legislationType: 'Family Law Reform',
          legislationJurisdiction: stateName,
        },
      };
    }),
  };

  // Generate WebPage schema
  const webPageSchema = generateWebPageSchema({
    name: `RobertGarza.us Family Court Reform - ${stateName} Family Law Bills`,
    description: `Explore family law reform bills for ${stateName}. Comprehensive judicial reform legislation aimed at improving family court systems.`,
    url: `/bill/${country}/${state}`,
  });

  // Combine schemas
  const schema = stateBills.length > 0 ? [webPageSchema, itemListSchema as any] : webPageSchema;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container component="main" sx={{ py: 8 }}>
        <SEO
          title={`RobertGarza.us Family Court Reform - ${stateName} Bills`}
          description={`Family law reform bills for ${stateName}`}
          path={`/bill/${country}/${state}`}
          schema={schema}
        />
        <Typography variant="h2" sx={{ textAlign: 'center', mb: 4, color: 'error.main' }}>
          Error Loading Bills
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center' }}>
          We encountered an error loading bills for {stateName}. Please try again later.
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <SEO
        title={`RobertGarza.us Family Court Reform - ${stateName} Family Law Bills`}
        description={`${stateName} has ${stateBills.length} family law reform bills addressing parental rights, court transparency, child support equity, and judicial accountability. Modern legislative proposals to improve family court systems and ensure fair treatment for all parents.`}
        path={`/bill/${country}/${state}`}
        schema={schema}
      />

      {/* Breadcrumbs */}
      <Box sx={{ pt: 0.5, pb: 1, display: 'flex', justifyContent: 'center' }}>
        <Breadcrumbs items={breadcrumbs} />
      </Box>

      <Container component="main" sx={{ py: 8 }}>
        {/* State Header with Icon */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          {/* State SVG Map — only show when there are bills to display */}
          {stateBills.length > 0 && (
            <Box
              sx={{
                mb: 4,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <StateShape stateName={stateName} country={country} color="#FFD700" size={400} />
            </Box>
          )}
          <Typography variant="h2" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
            {stateName}
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
            Family Law Reform Bills
          </Typography>

          {/* Key Stats - AI-optimized */}
          {stateBills.length > 0 && (
            <Box sx={{ display: 'flex', gap: 6, justifyContent: 'center', mb: 4, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {stateBills.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Reform Bills
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {new Set(stateBills.map((b) => b.billTitle.split(/\s+/).slice(0, 3).join(' '))).size}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Focus Areas
                </Typography>
              </Box>
            </Box>
          )}

          {/* Summary Paragraph - AI-optimized answer-first format */}
          {stateBills.length > 0 && (
            <Typography
              variant="body1"
              sx={{
                mb: 4,
                textAlign: 'center',
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.7,
                color: 'text.secondary',
              }}
            >
              {stateName} has {stateBills.length} family law reform bills addressing parental rights, court
              transparency, child support equity, and judicial accountability. These legislative proposals aim to
              modernize family court systems and ensure fair treatment for all parents and families.
            </Typography>
          )}
        </Box>

        {/* Bills Grid */}
        {stateBills.length > 0 ? (
          <Grid container spacing={4}>
            {stateBills.map((bill) => {
              const billSlug = resolveBillRoute(bill.billTitle, billPages);

              const billImage = heroImageMap[billSlug] || heroImageMap._default;

              return (
                <Grid item xs={12} sm={6} md={4} key={bill.billId || billSlug}>
                  <Card sx={{ boxShadow: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardMedia
                      component="img"
                      height="250"
                      image={billImage}
                      alt={bill.billTitle}
                      loading="lazy"
                      sx={{
                        objectFit:
                          billSlug === 'parent-child-restoration-act' ||
                          billSlug === 'judicial-accountability-referral-act'
                            ? 'contain'
                            : 'cover',
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      {(() => {
                        const { color, label } = getBillStatusChip(bill.billStatus);
                        return (
                          <Chip
                            label={label}
                            variant="outlined"
                            size="small"
                            color={color}
                            sx={{ mb: 1, alignSelf: 'flex-start' }}
                          />
                        );
                      })()}
                      <Typography
                        component="h3"
                        sx={{ fontWeight: 'bold', mb: 1.5, fontSize: '1rem', lineHeight: 1.4 }}
                      >
                        {bill.billTitle}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1, lineHeight: 1.6 }}>
                        {((text: string) => (text.length > 100 ? `${text.slice(0, 100)}…` : text))(
                          shortDescriptionByTitle[bill.billTitle.toLowerCase()] ||
                            'Family law reform legislation to improve court transparency and parental rights.',
                        )}
                      </Typography>
                      <NavigateToButton
                        onClick={() => {}}
                        to={`/bill/${country}/${state}/${billSlug}`}
                        sx={{ width: '100%', mt: 'auto' }}
                      >
                        Learn More
                      </NavigateToButton>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h4" sx={{ mb: 2, color: 'text.secondary' }}>
              This state has no bills
            </Typography>
            <Typography variant="body1" color="text.secondary">
              There are currently no bills available for {stateName}. Check back later for updates.
            </Typography>
          </Box>
        )}
      </Container>
    </>
  );
};

export default StateBillsPage;
